import React from 'react'
import { Button } from '@toasttab/buffet-pui-buttons'
import cx from 'classnames'
import { useFormatCurrency } from '../../../hooks/useFormatCurrency'
import { track } from '@toasttab/do-secundo-analytics'
import { AnalyticsEvents } from '../../../utils/analyticsEvents'
import { useBanquetProps, useIntlProps } from 'banquet-runtime-modules'
import { PaymentType } from '../../../api/payment/model'
import { useAuth } from '@toasttab/do-secundo-guest-authentication'

type TimeSlotButtonProps = {
  timeSlot: string
  timeSlotIndex: number
  totalTimeSlots: number
  formattedTime?: string
  serviceAreaGroupName: string
  paymentAmount?: number
  paymentType?: PaymentType
  onTimeSlotSelect: () => void
}

export const TimeSlotButton = ({
  timeSlot,
  timeSlotIndex,
  totalTimeSlots,
  formattedTime,
  serviceAreaGroupName,
  paymentAmount,
  paymentType,
  onTimeSlotSelect
}: TimeSlotButtonProps) => {
  const spiEnabled = Boolean(
    useBanquetProps().featureFlags?.['nv1-spi-enabled']
  )
  const { isAuthenticated } = useAuth()
  const { language: locale } = useIntlProps()

  const handleClick = () => {
    track(AnalyticsEvents.CLICK_AVAILABLE_TIME, {
      deposit: paymentAmount ?? 0,
      payment: paymentAmount ?? 0,
      paymentRequired: (paymentAmount ?? 0) > 0,
      isAuthenticated: isAuthenticated,
      spiEnabled: spiEnabled
    })

    onTimeSlotSelect()
  }

  const formatCurrency = useFormatCurrency()

  const paymentAmountLabel = paymentAmount
    ? (() => {
        switch (paymentType) {
          case PaymentType.DEPOSIT:
            return ` ${formatCurrency(paymentAmount)} Deposit`
          case PaymentType.PREPAYMENT:
            return ` ${formatCurrency(paymentAmount)} Total`
          default:
            return ` ${formatCurrency(paymentAmount)}`
        }
      })()
    : null

  return (
    <div key={timeSlot} className='flex flex-col'>
      <Button
        variant='secondary'
        size='sm'
        className={cx({
          'w-[101px]': ['en-US'].includes(locale),
          'w-[102px]': ['en-IE', 'en-GB'].includes(locale),
          'w-[112px]': ['en-CA', 'es-US'].includes(locale),
          'ml-4 sm:ml-0': timeSlotIndex === 0,
          'mr-4 sm:mr-0': timeSlotIndex === totalTimeSlots - 1
        })}
        onClick={handleClick}
        testId={`TimeSlot-${serviceAreaGroupName}-${formattedTime}`}
        aria-label={
          `Time slot in ${serviceAreaGroupName} at ${formattedTime}` +
          (paymentAmountLabel ? ` with ${paymentAmountLabel}` : '')
        }
      >
        {formattedTime}
      </Button>
      {paymentAmountLabel && (
        <span className='type-caption text-center mt-0.5'>
          {paymentAmountLabel}
        </span>
      )}
    </div>
  )
}

export default TimeSlotButton
